// A single, lazy, in-browser store that mirrors the shape of the real backend.
// Used by `mockAdapter` when running the module standalone (no CoreMonit API
// available). Every collection is generated once with a deterministic seed so
// screenshots and E2E specs are stable.
//
// When the module is merged into apps/customer-app, this file is untouched —
// the adapter is only invoked when `VITE_MICROSERVICES_MOCK !== "0"`. See
// docs/FRONTEND_INTEGRATION.md for the switch-over.

import {
  generateServices, buildOverview, generateDeployments, manifestDiff,
  generatePods, generateTraces, generateLogs, clusterLogs, buildMetrics,
  buildDependencyGraph, generateRules, generateIncidents, generateRCA,
  healthFor, slosFor, generateCertificates, generateSecrets,
  generateNetworkPolicies, generateVulnerabilities, generateSBOM,
  generateFlamegraph, hotMethodsFor, slowQueriesFor,
  generateProjects, buildInfrastructureTree,
} from "@coremonit/mock-data";
import type {
  Service, Deployment, Pod, Trace, Span, LogLine,
  AlertRule, Incident, DependencyGraph, RCACause,
  Certificate, Secret, NetworkPolicy, VulnerabilityFinding, SBOMComponent,
  FlameNode, HotMethod, SlowQuery, HealthReport, SLO, ServiceMetrics,
  Project, ProjectGroup, ProjectMember, InfrastructureTree,
} from "@coremonit/types";

interface Store {
  services:    Service[];
  deployments: Deployment[];
  podsByService: Map<string, Pod[]>;
  traces:      Trace[];
  spansByTrace: Map<string, Span[]>;
  logs:        LogLine[];
  metrics:     Map<string, ServiceMetrics>;
  rules:       AlertRule[];
  incidents:   Incident[];
  rcaByService: Map<string, RCACause[]>;
  graph:       DependencyGraph;
  slos:        Map<string, SLO[]>;
  health:      Map<string, HealthReport>;
  certs:       Certificate[];
  secrets:     Secret[];
  netpols:     NetworkPolicy[];
  vulns:       VulnerabilityFinding[];
  sbom:        SBOMComponent[];
  flames:      Map<string, FlameNode>;
  hotMethods:  Map<string, HotMethod[]>;
  slowQueries: SlowQuery[];
  projects:    Project[];
  groups:      ProjectGroup[];
  members:     ProjectMember[];
  hierarchy:   InfrastructureTree;
}

let cached: Store | null = null;

export function getMockStore(): Store {
  if (cached) return cached;
  const services   = generateServices();
  // Assign every service to a project + auto-attach to the matching group(s).
  const projectBundle = generateProjects(services);
  for (const svc of services) {
    const pid = projectBundle.serviceToProject.get(svc.id);
    if (pid) svc.projectId = pid;
    // Group id = the first matching group whose targets include this service.
    const matches = projectBundle.groups.filter((g) => {
      if (g.projectId !== pid) return false;
      if (g.kind === "environment") return g.targets.includes(svc.environment);
      if (g.kind === "provider")    return g.targets.includes(svc.deployment.type);
      if (g.kind === "cluster")     return !!svc.deployment.cluster && g.targets.includes(svc.deployment.cluster);
      if (g.kind === "region")      return !!svc.deployment.region  && g.targets.includes(svc.deployment.region);
      if (g.kind === "custom")      return g.targets.includes(svc.id);
      return false;
    });
    if (matches[0]) svc.groupId = matches[0].id;
  }
  const graph      = buildDependencyGraph(services);
  const allPods    = generatePods(services);
  const podsByService = new Map<string, Pod[]>();
  for (const p of allPods) {
    const bucket = podsByService.get(p.serviceId) ?? [];
    bucket.push(p);
    podsByService.set(p.serviceId, bucket);
  }
  const t = generateTraces(services);
  const deployments = generateDeployments(services);

  const store: Store = {
    services,
    deployments,
    podsByService,
    traces:       t.traces,
    spansByTrace: t.spansByTrace,
    logs:         generateLogs(services),
    metrics:      new Map(services.map((s) => [s.id, buildMetrics(s)])),
    rules:        generateRules(services),
    incidents:    generateIncidents(services),
    rcaByService: new Map(services.map((s) => [s.id, generateRCA(s)])),
    graph,
    slos:         new Map(services.map((s) => [s.id, slosFor(s)])),
    health:       new Map(services.map((s) => [s.id, healthFor(s)])),
    certs:        generateCertificates(services),
    secrets:      generateSecrets(services),
    netpols:      generateNetworkPolicies(services),
    vulns:        generateVulnerabilities(services),
    sbom:         generateSBOM(),
    flames:       new Map(services.map((s) => [s.id, generateFlamegraph()])),
    hotMethods:   new Map(services.map((s) => [s.id, hotMethodsFor(s)])),
    slowQueries:  slowQueriesFor(services),
    projects:     projectBundle.projects,
    groups:       projectBundle.groups,
    members:      projectBundle.members,
    hierarchy:    buildInfrastructureTree(services, projectBundle.projects, podsByService, deployments),
  };
  cached = store;
  return store;
}

export function overviewSummary() {
  return buildOverview(getMockStore().services);
}

export function clusteredLogs() {
  return clusterLogs(getMockStore().logs);
}

export function diffFor(_id: string) {
  return { diff: manifestDiff() };
}
